// ============================================================================
// Uploads (docs/areas/files.md): straight from the device to the bucket. Small
// files are one presigned PUT; anything past SINGLE_PUT_MAX is a multipart
// upload cut by planParts (the server's files.PartPlan), three parts in flight,
// each retried with backoff, and resumable: what the bucket already holds is
// asked for (GET /api/files/uploads/parts) and skipped. Cancelling aborts the
// multipart upload so its parts do not linger.
//
// The sha256 of the bytes is computed on the device as they are read; up to
// DEDUPE_MAX it is computed first, so a file whose bytes are already in the
// bucket is not sent again (the new File points at the same object). The pure
// parts (planParts, partRange, backoff) are tested in upload.test.ts.
// ============================================================================

import { sha256 } from '@noble/hashes/sha2.js'
import { bytesToHex } from '@noble/hashes/utils.js'
import { filesApi, type UploadedPart } from './api'

export const SINGLE_PUT_MAX = 64 * 1024 * 1024
export const MIN_PART = 16 * 1024 * 1024
export const MAX_PARTS = 10_000
/** Files up to this size are hashed before they are sent, so a duplicate is never sent. */
export const DEDUPE_MAX = 512 * 1024 * 1024
/** How much of a file is read at once while hashing. */
const READ_CHUNK = 8 * 1024 * 1024
const PARALLEL = 3
const TRIES = 5

/** Mirrors files.PartPlan: 16 MiB parts until a file needs more than 10,000, then whole MiB. */
export const planParts = (size: number): { partSize: number; count: number } => {
  if (size <= 0) return { partSize: 0, count: 0 }
  let partSize = MIN_PART
  const need = Math.ceil(size / MAX_PARTS)
  if (need > partSize) partSize = Math.ceil(need / (1024 * 1024)) * 1024 * 1024
  return { partSize, count: Math.ceil(size / partSize) }
}

/** Byte range [start, end) of part n (1-based). */
export const partRange = (n: number, partSize: number, size: number): [number, number] => [
  (n - 1) * partSize,
  Math.min(n * partSize, size),
]

/** Wait before try `attempt` (1-based): 1 s, 2 s, 4 s, … capped at 20 s. */
export const backoff = (attempt: number): number => Math.min(20_000, 1000 * 2 ** (attempt - 1))

/** The parts still to send, given what the bucket already holds. */
export const partsToSend = (count: number, have: readonly UploadedPart[]): number[] => {
  const done = new Set(have.map((p) => p.n))
  const out: number[] = []
  for (let n = 1; n <= count; n++) if (!done.has(n)) out.push(n)
  return out
}

export class UploadCancelled extends Error {
  constructor() {
    super('cancelled')
    this.name = 'UploadCancelled'
  }
}

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const t = setTimeout(resolve, ms)
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(t)
        reject(new UploadCancelled())
      },
      { once: true },
    )
  })

/** sha256 of a file, read in chunks so memory stays flat; progress in bytes read. */
export const hashFile = async (
  file: Blob,
  onProgress?: (read: number) => void,
  signal?: AbortSignal,
): Promise<string> => {
  const h = sha256.create()
  for (let at = 0; at < file.size; at += READ_CHUNK) {
    if (signal?.aborted) throw new UploadCancelled()
    const buf = new Uint8Array(await file.slice(at, at + READ_CHUNK).arrayBuffer())
    h.update(buf)
    onProgress?.(Math.min(at + READ_CHUNK, file.size))
  }
  return bytesToHex(h.digest())
}

/**
 * PUT bytes to a presigned URL with progress (XHR: fetch has no upload
 * progress). Answers the ETag the bucket gave, which a multipart upload needs.
 */
const put = (url: string, body: Blob, onProgress: (sent: number) => void, signal?: AbortSignal) =>
  new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.upload.onprogress = (e) => onProgress(e.loaded)
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.getResponseHeader('ETag') ?? '')
      else reject(new Error(`the bucket said ${xhr.status}`))
    }
    xhr.onerror = () => reject(new Error('the upload was cut off'))
    xhr.onabort = () => reject(new UploadCancelled())
    signal?.addEventListener('abort', () => xhr.abort(), { once: true })
    xhr.send(body)
  })

/** A form POST to the bucket (a file request's upload: the server's POST policy decides what it takes). */
export const postForm = (
  url: string,
  fields: Record<string, string>,
  file: Blob,
  onProgress: (sent: number) => void,
  signal?: AbortSignal,
) =>
  new Promise<void>((resolve, reject) => {
    const form = new FormData()
    for (const [k, v] of Object.entries(fields)) form.append(k, v)
    form.append('file', file) // last: S3 ignores fields after the file
    const xhr = new XMLHttpRequest()
    xhr.open('POST', url)
    xhr.upload.onprogress = (e) => onProgress(Math.min(e.loaded, file.size))
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`the bucket said ${xhr.status}`))
    xhr.onerror = () => reject(new Error('the upload was cut off'))
    xhr.onabort = () => reject(new UploadCancelled())
    signal?.addEventListener('abort', () => xhr.abort(), { once: true })
    xhr.send(form)
  })

const withRetry = async <T>(what: () => Promise<T>, signal?: AbortSignal): Promise<T> => {
  for (let attempt = 1; ; attempt++) {
    try {
      return await what()
    } catch (e) {
      if (e instanceof UploadCancelled || signal?.aborted) throw new UploadCancelled()
      if (attempt >= TRIES) throw e
      await sleep(backoff(attempt), signal)
    }
  }
}

export interface SendOptions {
  key: string
  file: Blob
  contentType: string
  /** total bytes sent so far */
  onProgress: (sent: number) => void
  signal?: AbortSignal
  /** a multipart upload already started for this key (resuming after a reload) */
  uploadId?: string
  /** told the multipart upload's id as soon as there is one, so it can be resumed or aborted */
  onUploadId?: (id: string) => void
}

/** Send a file's bytes to the bucket under `key`. Resolves once the object exists. */
export const sendBytes = async (o: SendOptions): Promise<void> => {
  const { key, file, signal } = o
  if (!o.uploadId) {
    const start = await withRetry(() => filesApi.uploadStart(key, file.size, o.contentType), signal)
    if ('url' in start) {
      await withRetry(() => put(start.url, file, o.onProgress, signal), signal)
      o.onProgress(file.size)
      return
    }
    o.uploadId = start.upload_id
    o.onUploadId?.(start.upload_id)
  }
  const uploadId = o.uploadId
  const { partSize, count } = planParts(file.size)
  const have = (await withRetry(() => filesApi.uploadedParts(key, uploadId), signal)).parts
  const done: UploadedPart[] = have.filter((p) => p.n <= count)
  const inFlight = new Map<number, number>()
  let base = done.reduce(
    (s, p) => s + (p.size ?? partRange(p.n, partSize, file.size)[1] - partRange(p.n, partSize, file.size)[0]),
    0,
  )
  const report = () => o.onProgress(base + [...inFlight.values()].reduce((a, b) => a + b, 0))
  report()
  const queue = partsToSend(count, done)
  const worker = async () => {
    while (queue.length) {
      if (signal?.aborted) throw new UploadCancelled()
      const batch = queue.splice(0, 1)
      const n = batch[0]!
      const [from, to] = partRange(n, partSize, file.size)
      const etag = await withRetry(async () => {
        // a fresh URL each try: an old one may have expired on a long upload
        const { urls } = await filesApi.uploadParts(key, uploadId, [n])
        inFlight.set(n, 0)
        return put(
          urls[String(n)]!,
          file.slice(from, to),
          (sent) => {
            inFlight.set(n, sent)
            report()
          },
          signal,
        )
      }, signal)
      inFlight.delete(n)
      base += to - from
      done.push({ n, etag })
      report()
    }
  }
  await Promise.all(Array.from({ length: Math.min(PARALLEL, queue.length || 1) }, worker))
  await withRetry(() => filesApi.uploadComplete(key, uploadId, done), signal)
}
